import React from 'react'
import { alpha, Box, Typography, useTheme } from '@mui/material'
import { useLocation, useNavigate } from 'react-router-dom'
import { CategoryIcon, HomeIcon, UserIcon } from '@/components/icons'
import { designTokens } from '@/theme'
import { getDesignTokens } from '@/theme/designTokens'
import { REACT_STABLE_KEY_TYPE, buildStableKey } from '@/utils/react-key.util'
import {
  storefrontJoin,
  stripStorefrontPrefix,
  useStorefrontBasePath,
} from '@/utils/storefront-paths'

type NavItem = {
  key: string
  label: string
  path: string
  icon: React.ReactNode
  matches: (appPath: string) => boolean
}

const NAV_ITEMS: NavItem[] = [
  {
    key: 'home',
    label: 'Home',
    path: '/',
    icon: <HomeIcon />,
    matches: (appPath) => appPath === '/',
  },
  {
    key: 'dishes',
    label: 'Menu',
    path: '/dishes',
    icon: <CategoryIcon />,
    matches: (appPath) => appPath.startsWith('/dishes'),
  },
  {
    key: 'profile',
    label: 'Profile',
    path: '/my-account',
    icon: <UserIcon />,
    matches: (appPath) => appPath.startsWith('/my-account'),
  },
]

function BottomNavBar(): React.ReactElement {
  const theme = useTheme()
  const tokens = getDesignTokens(theme)
  const navigate = useNavigate()
  const { pathname } = useLocation()
  const basePath = useStorefrontBasePath()
  const appPath = stripStorefrontPrefix(pathname)

  return (
    <Box
      data-layer="bottom-nav"
      sx={{
        display: { xs: 'flex', md: 'none' },
        width: '100%',
        px: { xs: 1.5, sm: 2 },
        // Safe area stays on the nav — do not remove.
        // The floor was 0px, which made the max() a no-op: on any surface reporting no inset
        // (Android, and iOS Safari where its own toolbar occupies the space) the bar sat flush
        // against the bottom edge. A 5px floor lifts it there without adding anything on a
        // device that already reports a home-indicator inset, so the gap above the indicator
        // is unchanged.
        pb: 'max(env(safe-area-inset-bottom, 0px), 5px)',
        pt: 0,
        pointerEvents: 'auto',
      }}
    >
      <Box
        sx={{
          width: '100%',
          height: 64,
          borderRadius: `${designTokens.shape.borderRadius.xl}px`,
          overflow: 'hidden',
          display: 'grid',
          gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
          backgroundColor: theme.palette.background.paper,
          boxShadow: '0 10px 28px rgba(2, 6, 23, 0.22)',
          border: `1px solid ${theme.palette.divider}`,
        }}
      >
        {NAV_ITEMS.map((item, navIdx) => {
          const active = item.matches(appPath)
          return (
            <Box
              key={buildStableKey(REACT_STABLE_KEY_TYPE.bottomNav, navIdx, item.key, item.path)}
              component="button"
              type="button"
              onClick={() => navigate(storefrontJoin(basePath, item.path))}
              aria-label={`Navigate to ${item.label}`}
              sx={{
                border: 0,
                background: 'transparent',
                color: active ? tokens.navActive.text : theme.palette.text.secondary,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 0.5,
                cursor: 'pointer',
                transition: `transform ${designTokens.motion.fast}, color ${designTokens.motion.normal}, background ${designTokens.motion.normal}`,
                ...(active && {
                  backgroundColor: tokens.navActive.surface,
                  boxShadow: `inset 0 0 0 1px ${tokens.navActive.border}`,
                }),
                '& svg': {
                  width: 22,
                  height: 22,
                },
                '&:hover': {
                  backgroundColor: alpha(theme.palette.text.primary, 0.04),
                },
                '&:active': {
                  transform: 'scale(0.97)',
                },
              }}
            >
              {item.icon}
              <Typography
                sx={{
                  fontSize: theme.typography.caption.fontSize,
                  fontWeight: active ? 600 : 500,
                  color: 'inherit',
                }}
              >
                {item.label}
              </Typography>
            </Box>
          )
        })}
      </Box>
    </Box>
  )
}

export default React.memo(BottomNavBar)
